import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { Theme, useTheme } from "@mui/material";
import Link from "next/link";
import { useState } from "react";

import DatabaseMenu from "~/components/database/detail/database-content/database-menu";
import {
  fixedHeight,
  fixedWidth,
  flexColumn,
  flexRow,
} from "~/lib/client/design";
import { engFont } from "~/lib/client/fonts";
import { formatDate } from "~/lib/graphql/graphqlClient";
import { DatabaseDetailPageProps } from "~/pages/database/[id]";

const SpDatabaseHeader = ({
  openModal,
  form,
}: {
  openModal: () => void;
  form: DatabaseDetailPageProps["form"];
}) => {
  const muiTheme = useTheme();

  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const closeMenu = () => setMenuIsOpen(false);
  const toggleMenu = () => setMenuIsOpen((prev) => !prev);

  return (
    <div>
      <div css={formIdStyle(muiTheme)}>#{form.id}</div>
      <CategoriesContainer>
        {form.categories.map((category) => (
          <Link
            href={`/category/${category.id}`}
            key={category.id}
            css={linkStyle(muiTheme)}
          >
            #{category.name}
          </Link>
        ))}
      </CategoriesContainer>
      <TitleContainer>{form.title}</TitleContainer>
      <hr />
      <DescriptionContainer>{form.description}</DescriptionContainer>
      <DatabaseMenuContainer>
        <DatabaseMenu
          form={form}
          menuIsOpen={menuIsOpen}
          closeMenu={closeMenu}
          toggleMenu={toggleMenu}
        />
      </DatabaseMenuContainer>
      <WriteButtonAndMetaInfoContainer>
        <WriteButton onClick={openModal} muiTheme={muiTheme}>
          書き込む
        </WriteButton>
        <MetaInfoContainer muiTheme={muiTheme}>
          <DateAndCreatedByContainer>
            <p>投稿数: {form.responseSize}</p>
            <p>作成者：{form.user.name}</p>
          </DateAndCreatedByContainer>
          <p>作成日：{formatDate(form.createdAt)}</p>
        </MetaInfoContainer>
      </WriteButtonAndMetaInfoContainer>
    </div>
  );
};

const formIdStyle = (theme: Theme) => css`
  ${flexRow};
  font-size: 1.5rem;
  justify-content: center;
  border-bottom: 1px solid ${theme.palette.divider};
  font-family: ${engFont.style.fontFamily};
  margin-top: 1rem;
  padding-bottom: 0.5rem;
`;

const CategoriesContainer = styled.div`
  ${flexRow};
  flex-wrap: wrap;
  gap: 0.5rem;
  margin: 1rem;
`;

const linkStyle = (muiTheme: Theme) => css`
  color: ${muiTheme.palette.primary.main};
  font-size: 14px;
`;

const TitleContainer = styled.h1`
  padding: 1rem;
  font-size: 20px;
  font-weight: 600;
`;

const DescriptionContainer = styled.p`
  font-size: 14px;
  padding: 1rem;
  white-space: pre-wrap;
  word-wrap: break-word;
  word-break: break-all;
  overflow-wrap: break-word;
  hyphens: auto;
  line-height: 1.5;
`;

const DatabaseMenuContainer = styled.div`
  margin-top: 0.5rem;
  margin-bottom: 0.5rem;
  margin-right: 1rem;
`;

const WriteButtonAndMetaInfoContainer = styled.div`
  ${flexRow};
  ${fixedHeight("3rem")};
`;

const WriteButton = styled.button<{ muiTheme: Theme }>`
  ${fixedWidth("8.75rem")};
  ${fixedHeight("3rem")};

  display: flex;
  align-items: center;
  justify-content: center;

  background: ${({ muiTheme }) => muiTheme.palette.primary.main};
  color: ${({ muiTheme }) => muiTheme.palette.primary.contrastText};
  font-size: 15px;

  border: none;
`;

const MetaInfoContainer = styled.div<{ muiTheme: Theme }>`
  ${flexRow};
  flex: 1;
  gap: 0.5rem;
  background: ${({ muiTheme }) => muiTheme.palette.background.paper};
  color: ${({ muiTheme }) => muiTheme.palette.text.primary};

  padding-top: 0.375rem;

  p {
    margin: 0;
    font-size: 10px;
  }
`;

const DateAndCreatedByContainer = styled.div`
  ${flexColumn};
  gap: 0.25rem;
  margin-left: 1rem;
`;

export default SpDatabaseHeader;
